import { useEffect, useState } from "react"
import { useNavigate, useLocation, useSearchParams } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import { createDevShortcutSession } from "~/api/assessmentSessions"
import type { AssessmentSessionResponse } from "~/api/assessmentSessions"
import { getPortraitStatus } from "~/api/portrait"
import { PageShell } from "~/components/PageShell"
import { assetUrl } from "~/utils/assetUrl"

const MIN_PROCESSING_DISPLAY_MS = 8500
const PORTRAIT_POLL_MS = 2000
const PORTRAIT_MAX_WAIT_MS = 90_000

const PHRASE_INTERVAL_MS = 2000

const phraseFade = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
}

export function ComputingResultsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const state = location.state as {
    sessionToken?: string
    result?: unknown
    addDog?: boolean
    photoAttached?: boolean
  } | null
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0)
  const [devError, setDevError] = useState<string | null>(null)
  const result = state?.result as { dog_name?: string } | undefined

  const devType = import.meta.env.DEV && !state ? searchParams.get("type") : null
  const dogName =
    result?.dog_name?.trim() || searchParams.get("name")?.trim() || "your dog"
  const waitForPortrait = Boolean(state?.addDog && state?.photoAttached)
  const phrases = state?.addDog
    ? [
        `Compiling ${dogName}'s responses…`,
        `Reading ${dogName}'s personality signals…`,
        `Adding ${dogName} to your pack…`,
        waitForPortrait
          ? `Creating ${dogName}'s personalised portrait…`
          : `Building ${dogName}'s Doggy Dynamo profile…`,
        `${dogName}'s Doggy Dynamo profile is almost ready.`,
      ]
    : [
        `Compiling ${dogName}'s responses…`,
        `Reading ${dogName}'s personality signals…`,
        "This is where things get interesting…",
        `Building ${dogName}'s Doggy Dynamo profile…`,
        "Almost ready…",
      ]

  useEffect(() => {
    let cancelled = false

    if (devType) {
      let minDisplayElapsed = false
      let devResult: AssessmentSessionResponse | null = null

      const maybeNavigate = () => {
        if (cancelled || !minDisplayElapsed || !devResult) return
        navigate(`/results?token=${encodeURIComponent(devResult.session_token)}`, {
          state: { sessionToken: devResult.session_token, result: devResult },
          replace: true,
        })
      }

      const minTimer = setTimeout(() => {
        minDisplayElapsed = true
        maybeNavigate()
      }, MIN_PROCESSING_DISPLAY_MS)

      createDevShortcutSession(devType, searchParams.get("name") ?? undefined)
        .then((result) => {
          devResult = result
          maybeNavigate()
        })
        .catch((err) => {
          if (!cancelled) {
            setDevError(err instanceof Error ? err.message : "Dev shortcut failed")
          }
        })

      return () => {
        cancelled = true
        clearTimeout(minTimer)
      }
    }

    let minElapsed = false
    let portraitReady = !waitForPortrait
    const startedAt = Date.now()

    const goNext = () => {
      if (cancelled || !minElapsed || !portraitReady) return
      if (state?.addDog && !state?.photoAttached) {
        navigate("/pack/add/photo", { state: state ?? undefined, replace: true })
        return
      }
      const sessionToken =
        state?.sessionToken ??
        (state?.result as { session_token?: string } | undefined)?.session_token
      const path = sessionToken
        ? `/results?token=${encodeURIComponent(sessionToken)}`
        : "/results"
      navigate(path, { state: state ?? undefined, replace: true })
    }

    const minTimer = setTimeout(() => {
      minElapsed = true
      goNext()
    }, MIN_PROCESSING_DISPLAY_MS)

    let pollTimer: number | undefined
    if (waitForPortrait) {
      const poll = async () => {
        if (cancelled) return
        try {
          const data = await getPortraitStatus()
          if (data.portrait?.status === "ready" || data.portrait?.status === "failed") {
            portraitReady = true
            goNext()
            return
          }
        } catch {
          // keep polling until timeout
        }
        if (Date.now() - startedAt >= PORTRAIT_MAX_WAIT_MS) {
          portraitReady = true
          goNext()
          return
        }
        pollTimer = window.setTimeout(() => {
          void poll()
        }, PORTRAIT_POLL_MS)
      }
      void poll()
    }

    return () => {
      cancelled = true
      clearTimeout(minTimer)
      if (pollTimer) window.clearTimeout(pollTimer)
    }
  }, [navigate, state, devType, searchParams, waitForPortrait])

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length)
    }, PHRASE_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [phrases.length])

  return (
    <PageShell centerContent>
      <motion.div
        className="flex flex-col items-center gap-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="relative flex items-center justify-center">
          <motion.div
            className="absolute inset-0 rounded-full border-2 border-[var(--color-gold-400)]"
            animate={{
              scale: [1, 1.4, 1],
              opacity: [0.5, 0, 0.5],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.div
            className="relative flex h-28 w-28 items-center justify-center"
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          >
            <img
              src={assetUrl("/images/swish.png")}
              alt=""
              className="h-full w-full object-contain"
              aria-hidden
            />
          </motion.div>
        </div>

        <div className="flex min-h-[3rem] items-center justify-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={currentPhraseIndex}
              variants={phraseFade}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.4 }}
              className="text-center text-lg font-medium text-[var(--color-text-primary)]"
            >
              {phrases[currentPhraseIndex]}
            </motion.p>
          </AnimatePresence>
        </div>

        <p className="text-sm text-[var(--color-text-secondary)]">
          {dogName}&apos;s Doggy Dynamo profile is almost ready.
        </p>
        {devError && (
          <p className="text-sm text-red-600" role="alert">
            {devError}
          </p>
        )}
      </motion.div>
    </PageShell>
  )
}
